import { BillingInterval } from "./BillingInterval";
import axios from "axios";
import {
  Tier,
  Type,
  User,
  StripeInvoicePage,
  StripeSubscriptionDetail,
  WebhookEndpoint,
  WebhookEndpointWithSecret,
  WebhookEventType,
  WebhookDelivery,
  WebhookUsage,
} from "./types";

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_HOST || "http://localhost:8080/",
  withCredentials: true,
});

export const changeTrialCancellation = async (type: Type, trialEnd: string, action: "end_of_trial" | "immediate" | "undo") => {
  const res = await instance.post("/stripe/trial-cancellation", { type, trial_end: trialEnd, action });
  return res.data;
};

export const getSession = async (): Promise<User> => {
  const res = await instance.get("/session");
  return res.data;
};

export const signIn = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  const res = await instance.post("/signin", { email, password });
  return res.data;
};

export const resetPassword = async ({ email }: { email: string }) => {
  const res = await instance.post("/reset-password", { email });
  return res.data;
};

export const changePassword = async ({
  token,
  password,
}: {
  token: string;
  password: string;
}) => {
  const res = await instance.post("/change-password", { token, password });
  return res.data;
};

export const signUp = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  const res = await instance.post("/signup", { email, password });
  return res.data;
};

export const signOut = async () => {
  const res = await instance.post("/signout");
  return res.data;
};

export const createCheckoutSession = async (
  tier: Tier,
  type: Type,
  gaw?: string | null,
  gau?: string | null,
  ggs?: string | null
) => {
  // Translate because + is not allowed in URL
  let tierTranslated = tier === "paid+" ? "paid_plus" : tier;
  const res = await instance.post(
    `/stripe/create-checkout-session?tier=${tierTranslated}&type=${type}&gaw=${gaw}&gau=${gau}&ggs=${ggs}`,
    {
      google: {
        gaw,
        gau,
        ggs,
      },
    }
  );
  return res.data;
};

export const createManageSubscriptionSession = async (
  mode?: "billing_overview"
) => {
  const res = await instance.post(
    "/stripe/create-portal-session",
    mode ? { mode } : undefined
  );
  return res.data;
};

export const updateTier = async (tier: Tier, type: Type, billing_interval?: BillingInterval, preview_token?: string) => {
  let tierTranslated = tier === "paid+" ? "paid_plus" : tier;
  const res = await instance.post("/stripe/update-plan", {
    tier: tierTranslated,
    type, billing_interval, preview_token,
  });
  return res.data;
};

// Immediate cancel (skips cancel_at_period_end scheduling). Used by the
// "End trial now" UI affordance.
export const endSubscriptionImmediately = async (type: Type) => {
  const res = await instance.post("/stripe/update-plan", {
    tier: "free",
    type,
    immediate: true,
  });
  return res.data;
};

export const createCheckoutSessionV2 = async (tier: Tier, type: Type, billing_interval: BillingInterval = "month") => {
  const res = await instance.post<{ clientSecret: string }>(
    "/stripe/v2/create-checkout-session",
    {
      tier: tier === "paid+" ? "paid_plus" : tier,
      type, billing_interval,
    }
  );
  return res.data;
};

export type AccountPlanTier = {
  price: number;
  currency?: string;
  billing_period?: string;
  rate_limit: number;
  rate_limit_window_seconds: number;
  description?: string;
};

export type AccountPlan = {
  sport: Type;
  display_name: string;
  spec_url?: string;
  tiers: Record<string, AccountPlanTier>;
};

export const getAccountPlans = async (): Promise<AccountPlan[]> => {
  const res = await instance.get<{ data: AccountPlan[] }>("/account/v1/plans");
  return res.data.data;
};

// Trial eligibility: bulk returns all trial-eligible types; single-type returns
// the eligibility for one type. Response for the bulk variant:
//   [{ type, eligible, reason? }, ...]
export type TrialIneligibleReason =
  | "trial_used"
  | "already_paid_plus"
  | "has_all_access"
  | "unsupported_type"
  | "paid_upgrade_trial_active";

export type TrialEligibilityEntry = {
  type: Type;
  eligible: boolean;
  reason?: TrialIneligibleReason;
};

export const getTrialEligibility = async (): Promise<
  TrialEligibilityEntry[]
> => {
  const res = await instance.get("/stripe/trial-eligibility");
  return res.data;
};

// Starts a 7-day trial. Response is either:
//   { clientSecret }  — new Checkout session, render custom checkout UI
//   { upgraded: true } — app-managed paid-upgrade overlay; refresh session
export type CreateTrialResponse =
  | { clientSecret: string; upgraded?: undefined }
  | { upgraded: true; clientSecret?: undefined };

export const createTrialCheckoutSession = async (
  type: Type, billing_interval: BillingInterval = "month"
): Promise<CreateTrialResponse> => {
  const res = await instance.post<CreateTrialResponse>(
    "/stripe/v2/create-trial-checkout-session",
    { type, billing_interval }
  );
  return res.data;
};

export type ConvertTrialNowResponse = {
  status: "processing" | "converted" | "payment_pending";
};

export const convertTrialNow = async (
  type: Type
): Promise<ConvertTrialNowResponse> => {
  const res = await instance.post<ConvertTrialNowResponse>(
    "/stripe/v2/convert-trial-now",
    { type }
  );
  return res.data;
};

export const rotateApiKey = async (): Promise<User> => {
  const res = await instance.post("/rotate-api-key");
  return res.data;
};

export const reactivateSubscription = async (type: Type) => {
  const res = await instance.post("/stripe/reactivate-subscription", { type });
  return res.data;
};

export const getSubscriptionDetails = async (): Promise<
  StripeSubscriptionDetail[]
> => {
  const res = await instance.get("/stripe/subscription-details");
  return res.data;
};

export const getStripeInvoices = async (params?: {
  limit?: number;
  cursor?: string | null;
}): Promise<StripeInvoicePage> => {
  const query = new URLSearchParams();
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.cursor) query.set("cursor", params.cursor);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const res = await instance.get<StripeInvoicePage>(
    `/stripe/invoices${suffix}`
  );
  return res.data;
};

// Webhook API
export const getWebhookEventTypes = async (): Promise<WebhookEventType[]> => {
  const res = await instance.get("/webhooks/v1/event-types");
  return res.data.data;
};

export const getWebhookEndpoints = async (): Promise<WebhookEndpoint[]> => {
  const res = await instance.get("/webhooks/v1/endpoints");
  return res.data.data;
};

export const getWebhookEndpoint = async (
  id: string
): Promise<WebhookEndpoint> => {
  const res = await instance.get(`/webhooks/v1/endpoints/${id}`);
  return res.data.data;
};

export const createWebhookEndpoint = async (params: {
  url: string;
  description?: string;
  event_types: string[];
}): Promise<WebhookEndpointWithSecret> => {
  const res = await instance.post("/webhooks/v1/endpoints", params);
  return res.data.data;
};

export const updateWebhookEndpoint = async (
  id: string,
  params: {
    url?: string;
    description?: string;
    active?: boolean;
    event_types?: string[];
  }
): Promise<WebhookEndpoint> => {
  const res = await instance.patch(`/webhooks/v1/endpoints/${id}`, params);
  return res.data.data;
};

export const deleteWebhookEndpoint = async (id: string): Promise<void> => {
  await instance.delete(`/webhooks/v1/endpoints/${id}`);
};

export const rotateWebhookSecret = async (
  id: string
): Promise<WebhookEndpointWithSecret> => {
  const res = await instance.post(`/webhooks/v1/endpoints/${id}/rotate-secret`);
  return res.data.data;
};

export const testWebhookEndpoint = async (
  id: string
): Promise<{ success: boolean; status?: number; error?: string }> => {
  const res = await instance.post(`/webhooks/v1/endpoints/${id}/test`);
  return res.data;
};

export const getWebhookDeliveries = async (
  endpointId: string,
  params?: { cursor?: number; status?: string; sport?: string }
): Promise<{ data: WebhookDelivery[]; meta: { next_cursor?: number } }> => {
  const query = new URLSearchParams();
  if (params?.cursor) query.set("cursor", String(params.cursor));
  if (params?.status) query.set("status", params.status);
  if (params?.sport) query.set("sport", params.sport);
  const qs = query.toString();
  const res = await instance.get(
    `/webhooks/v1/endpoints/${endpointId}/deliveries${qs ? `?${qs}` : ""}`
  );
  return res.data;
};

export const getWebhookDelivery = async (
  deliveryId: number
): Promise<WebhookDelivery> => {
  const res = await instance.get(`/webhooks/v1/deliveries/${deliveryId}`);
  return res.data.data;
};

export const retryWebhookDelivery = async (
  deliveryId: number
): Promise<void> => {
  await instance.post(`/webhooks/v1/deliveries/${deliveryId}/retry`);
};

export const getWebhookUsage = async (): Promise<WebhookUsage> => {
  const res = await instance.get("/webhooks/v1/usage");
  return res.data.data;
};

export const getBillingAttention = async (): Promise<
  import("./types").BillingAttentionItem[]
> => (await instance.get("/stripe/billing-attention")).data;

export const prepareBillingInvoice = async (token: string): Promise<{ url: string }> =>
  (await instance.post("/stripe/prepare-billing-invoice", { token })).data;

export const cancelBillingSubscription = async (
  item: import("./types").BillingAttentionItem
) =>
  (
    await instance.post("/stripe/cancel-billing-subscription", {
      token: item.token,
      mode: item.cancellationMode,
    })
  ).data;

export const previewPlan = async (tier: Tier, type: Type, billing_interval: BillingInterval) => (await instance.post("/stripe/preview-plan", { tier: tier === "paid+" ? "paid_plus" : tier, type, billing_interval })).data;
export const undoPlanChange = async (type: Type) => (await instance.post("/stripe/undo-plan-change", { type })).data;
